import React, { useState, useEffect } from 'react';
import { View, Text, StyleSheet, Platform, ActivityIndicator } from 'react-native';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import { colores, fuentes } from '../../../styles/variablesCss';
import { BACKEND_URL, MONTO_MINIMO_ENVIO_GRATIS } from '../../../Config/Const';
import Storage from '../../../utils/Storage';
import { useUser } from '../../../context/UserContext';
import { useCheckoutContext } from '../../../context/CheckoutContext';
import { useCart } from '../../../context/CartContext';

const PagoCuentaEmpleado = ({ total: totalProp }) => {
  // Estados para manejar la consulta de saldo
  const { saldoCuenta, setSaldoCuenta, tipoEntrega, precioFleteDireccion, deseaFactura } = useCheckoutContext();
  const { totales, calcularTotal } = useCart();
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState(null);
  const [suficienteSaldo, setSuficienteSaldo] = useState(true);
  
  // Obtener datos del usuario del contexto
  const { mail, codUsu } = useUser();

  // Calcular el total dinámicamente incluyendo percepciones si corresponde
  const subtotal = totales.subtotal || calcularTotal();
  const descuentoTotal = totales.descuentos || 0;
  const percepciones = totales.percepciones || 0;
  
  // Agregar percepciones solo si el usuario desea factura A o B
  const percepcionesAplicadas = deseaFactura ? percepciones : 0;
  const totalProductos = subtotal - descuentoTotal + percepcionesAplicadas;
  
  // Calcular el flete final
  let fleteAMostrar = 0;
  if (totalProductos > MONTO_MINIMO_ENVIO_GRATIS) {
    fleteAMostrar = 0;
  } else {
    const fleteCarrito = totales.precioFlete || 0;
    const fleteDireccion = precioFleteDireccion || 0;
    fleteAMostrar = parseInt(tipoEntrega) === 2 
      ? Math.max(fleteCarrito, fleteDireccion) 
      : fleteCarrito;
  }
  
  // Total final incluyendo el flete
  const total = totalProductos + fleteAMostrar;

  // Cargar el saldo de la cuenta al montar el componente
  useEffect(() => {
    consultarSaldoCuenta();
  }, []);

  // Recalcular si hay saldo suficiente cuando cambia el total
  useEffect(() => {
    if (saldoCuenta && saldoCuenta.disponible) {
      if (parseFloat(saldoCuenta.disponible) >= parseFloat(total)) {
        setSuficienteSaldo(true);
      } else {
        setSuficienteSaldo(false);
      }
    }
  }, [total, deseaFactura, percepciones, saldoCuenta]);

  // Función para consultar el saldo de la cuenta del empleado
  const consultarSaldoCuenta = async () => {
    try {
      setCargando(true);
      setError(null);
      
      let emailUsuario = mail;
      let codigoUsuario = codUsu;
      
      // Obtener datos de autenticación si no están en el contexto
      if (!emailUsuario || !codigoUsuario) {
        if (Platform.OS === 'web') {
          emailUsuario = localStorage.getItem('mail') || '';
          codigoUsuario = localStorage.getItem('codUsu') || '';
        } else {
          emailUsuario = await Storage.getItem('mail') || '';
          codigoUsuario = await Storage.getItem('codUsu') || '';
        }
      }
      
      // Crear query params
      const queryParams = new URLSearchParams();
      if (emailUsuario) queryParams.append('mail', emailUsuario);
      if (codigoUsuario) queryParams.append('codUsu', codigoUsuario);
      
      // Realizar la llamada a la API
      const response = await fetch(`${BACKEND_URL}/rest/fin/saldoCuenta?${queryParams.toString()}`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
      });
      
      const data = await response.json();
      
      // Procesar la respuesta con la estructura correcta
      if (data && data.response && data.response.dsResultado && 
          data.response.dsResultado.dsResultado) {
        
        const dsResultado = data.response.dsResultado.dsResultado;
        
        // Verificar si la operación fue exitosa
        if (dsResultado.resultado && 
            dsResultado.resultado.length > 0 && 
            dsResultado.resultado[0].resultado === true) {
          
          // Extraer el saldo de la cuenta
          if (dsResultado.saldoCuenta && dsResultado.saldoCuenta.length > 0) {
            const saldoInfo = {
              disponible: dsResultado.saldoCuenta[0].saldoCuenta || 0,
              limite: dsResultado.saldoCuenta[0].limite || dsResultado.saldoCuenta[0].saldoCuenta || 0
            };
            
            setSaldoCuenta(saldoInfo);
            
            // Comprobar si hay saldo suficiente para la compra
            if (parseFloat(saldoInfo.disponible) >= parseFloat(total)) {
              setSuficienteSaldo(true);
            } else {
              setSuficienteSaldo(false);
            }
          } else {
            setError('No se encontró información de saldo en la respuesta');
            setSuficienteSaldo(false);
          }
        } else {
          const mensajeError = dsResultado.resultado?.[0]?.mensaje || 'Error al verificar saldo';
          setError(mensajeError);
          setSuficienteSaldo(false);
        }
      } else {
        setError('Error al obtener información de saldo');
        setSuficienteSaldo(false);
      }
    } catch (err) {
      setError(`Error de conexión: ${err.message}`);
      setSuficienteSaldo(false);
    } finally {
      setCargando(false);
    }
  };

  // Renderizar diferentes estados del componente
  if (cargando) {
    return (
      <View style={styles.container}>
        <ActivityIndicator size="large" color={colores.rojo} style={styles.cargando} />
        <Text style={styles.textoInfo}>Consultando saldo de cuenta...</Text>
      </View>
    );
  }

  if (error) {
    return (
      <View style={styles.container}>
        <MaterialCommunityIcons name="alert-circle" size={40} color={colores.rojo} />
        <Text style={styles.textoError}>{error}</Text>
        <Text style={styles.textoInfo}>No se pudo obtener el saldo de tu cuenta.</Text>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <View style={styles.header}>
        <MaterialCommunityIcons name="account-cash" size={24} color={colores.rojo} />
        <Text style={styles.titulo}>Cuenta Empleado/Directivo</Text>
      </View> 
      
      {saldoCuenta && (
        <View style={styles.saldoContainer}>
          <View style={styles.infoFila}>
            <Text style={styles.infoLabel}>Saldo Disponible:</Text>
            <Text style={styles.infoValor}>$ {parseFloat(saldoCuenta.disponible).toFixed(2)}</Text>
          </View>      
        </View>
      )}

      {!suficienteSaldo && saldoCuenta && (
        <View style={styles.alertaContainer}>
          <MaterialCommunityIcons name="alert" size={24} color={colores.error} />
          <Text style={styles.alertaTexto}>
            El saldo disponible no es suficiente para realizar esta compra.
          </Text>
        </View>
      )}
      
      {suficienteSaldo && saldoCuenta && (
        <View style={styles.confirmarContainer}>
          <MaterialCommunityIcons name="check-circle" size={24} color={colores.verde} />
          <Text style={styles.confirmarTexto}>
            Tu saldo es suficiente para realizar esta compra.
          </Text>
          <Text style={styles.confirmarSubtexto}>
            El monto se descontará de tu cuenta al finalizar la compra.
          </Text>
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    width: '100%',
    backgroundColor: colores.fondoClaro,
    borderRadius: 8,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  titulo: {
    fontSize: 18,
    fontFamily: fuentes.semiBold,
    color: colores.textoPrincipal,
    marginLeft: 10,
  },
  saldoContainer: {
    backgroundColor: colores.blanco,
    padding: 15,
    borderRadius: 6,
  },
  infoFila: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 10,
    paddingBottom: 10,
    borderBottomWidth: 1,
    borderBottomColor: colores.fondoClaro,
  },
  infoLabel: {
    fontSize: 16,
    fontFamily: fuentes.medium,
    color: colores.textoPrincipal,
  },
  infoValor: {
    fontSize: 16,
    fontFamily: fuentes.bold,
    color: colores.textoPrincipal,
  },
  alertaContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colores.fondoError || '#ffebee',
    padding: 15,
    borderRadius: 6,
  },
  alertaTexto: {
    marginLeft: 10,
    fontSize: 14,
    color: colores.error,
    fontFamily: fuentes.medium,
    flex: 1,
  },
  confirmarContainer: {
    flexDirection: 'column',
    alignItems: 'center',
    backgroundColor: colores.fondoExito || '#e8f5e9',
    padding: 15,
    borderRadius: 6,
    marginTop: 5,
  },
  confirmarTexto: {
    fontSize: 16,
    color: colores.verde || '#2e7d32',
    fontFamily: fuentes.semiBold,
    textAlign: 'center',
    marginTop: 5,
  },
  confirmarSubtexto: {
    fontSize: 14,
    color: colores.textoSecundario,
    fontFamily: fuentes.regular,
    textAlign: 'center',
    marginTop: 5,
  },
  cargando: {
    marginBottom: 10,
  },
  textoInfo: {
    fontSize: 14,
    color: colores.textoSecundario,
    textAlign: 'center',
    fontFamily: fuentes.regular,
  },
  textoError: {
    fontSize: 16,
    color: colores.error,
    textAlign: 'center',
    fontFamily: fuentes.semiBold,
    marginVertical: 10,
  },
});

export default PagoCuentaEmpleado;
  